import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { chatKey, filterColumn, targetId } from '@/components/chat/chatTarget';
import { MESSAGE_SELECT_FIELDS } from '@/components/chat/messageSelectFields';
import type { ChatMessage, ChatTarget } from '@/components/chat/types';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

type PinRow = {
  id: string;
  message_id: string;
  created_at: string;
  pinned_by: string | null;
};

/** A pin with the message it points at, or `null` while that message is still loading. */
export type ChatPin = PinRow & { message: ChatMessage | null };

/**
 * The pinned messages on one thread.
 *
 * Scoped per thread, unlike `useChatMute` which loads every mute the viewer holds in one
 * go. That works there because RLS narrows mutes to the viewer's own handful of rows;
 * pins are readable on every thread the viewer belongs to, which for an org account is
 * every thread of every event it runs.
 *
 * Read in two queries rather than a PostgREST embed. The embed would resolve -- there is
 * a single FK -- but this repo treats embeds as fragile with reason: the one in
 * useOrganizationPublicEvents wraps itself in an `embedError` check with a full two-query
 * fallback beneath it. A thread has a handful of pins at most, so the second round trip
 * is free, and reusing MESSAGE_SELECT_FIELDS means a pinned message is the same shape as
 * one in the thread and renders through the same code.
 *
 * Realtime, because two admins and a room full of volunteers is exactly the case where a
 * stale banner misleads -- someone takes down the old meeting point and half the event is
 * still reading it. Any change refetches rather than patching from the payload: a pin
 * INSERT carries no message body, and the set is tiny.
 */
export function useChatPins(target: ChatTarget | null, canPin: boolean) {
  const [pins, setPins] = useState<ChatPin[]>([]);
  const [loading, setLoading] = useState(true);

  const key = target ? chatKey(target) : null;
  const column = target ? filterColumn(target) : null;
  const id = target ? targetId(target) : null;

  // Read inside the callbacks so refetch's identity depends only on the thread.
  const targetRef = useRef(target);
  targetRef.current = target;

  const refetch = useCallback(async () => {
    if (!column || !id) {
      setPins([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('chat_pins')
      .select('id, message_id, created_at, pinned_by')
      .eq(column, id)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('useChatPins load failed', error);
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as PinRow[];
    if (rows.length === 0) {
      setPins([]);
      setLoading(false);
      return;
    }

    const { data: messageRows, error: messageError } = await supabase
      .from('messages_new')
      .select(MESSAGE_SELECT_FIELDS)
      .in(
        'id',
        rows.map((row) => row.message_id),
      );

    if (messageError) {
      logger.error('useChatPins message load failed', messageError);
    }

    const messageById = new Map<string, ChatMessage>();
    for (const row of (messageRows ?? []) as ChatMessage[]) messageById.set(row.id, row);

    // A pin whose message is missing is kept with message: null rather than dropped --
    // the row is real, and hiding it would make an unpin the user can see no way to do.
    setPins(rows.map((row) => ({ ...row, message: messageById.get(row.message_id) ?? null })));
    setLoading(false);
  }, [column, id]);

  useEffect(() => {
    setLoading(true);
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!key || !column || !id) return;

    const channel = supabase
      .channel(`chat_pins:${key}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_pins', filter: `${column}=eq.${id}` },
        () => {
          void refetch();
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [key, column, id, refetch]);

  const pinnedMessageIds = useMemo(
    () => new Set(pins.map((pin) => pin.message_id)),
    [pins],
  );

  const isPinned = useCallback(
    (messageId: string) => pinnedMessageIds.has(messageId),
    [pinnedMessageIds],
  );

  const pin = useCallback(
    async (message: ChatMessage, userId: string) => {
      const current = targetRef.current;
      if (!current || !canPin) return;

      const optimistic: ChatPin = {
        id: `optimistic:${message.id}`,
        message_id: message.id,
        created_at: new Date().toISOString(),
        pinned_by: userId,
        message,
      };
      // The menu closes on click; the banner should be there when it does.
      setPins((prev) => [optimistic, ...prev.filter((p) => p.message_id !== message.id)]);

      const { error } = await supabase.from('chat_pins').insert({
        message_id: message.id,
        [filterColumn(current)]: targetId(current),
        pinned_by: userId,
      });
      if (error) logger.error('useChatPins pin failed', error);
      void refetch();
    },
    [canPin, refetch],
  );

  const unpin = useCallback(
    async (messageId: string) => {
      const current = targetRef.current;
      if (!current || !canPin) return;

      setPins((prev) => prev.filter((p) => p.message_id !== messageId));

      const { error } = await supabase
        .from('chat_pins')
        .delete()
        .eq('message_id', messageId)
        .eq(filterColumn(current), targetId(current));
      if (error) logger.error('useChatPins unpin failed', error);
      void refetch();
    },
    [canPin, refetch],
  );

  return { pins, isPinned, pin, unpin, loading, refetch };
}
